import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

const STORAGE_KEY = "sama-browser-voice-command-settings";

const DEFAULT_SETTINGS = {
  enabled: false,
};

const DEFAULT_RUNTIME = {
  state: "off",
  live: false,
  level: 0,
  message: "",
  error: "",
  available: true,
};

const normalizeSettings = (value) => ({
  enabled: value?.enabled === true,
});

const readStoredSettings = () => {
  if (typeof window === "undefined") return DEFAULT_SETTINGS;

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? normalizeSettings(JSON.parse(raw)) : DEFAULT_SETTINGS;
  } catch (error) {
    console.error("Failed to read Hey Sama settings:", error);
    return DEFAULT_SETTINGS;
  }
};

const persistSettings = (settings) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
    console.error("Failed to save Hey Sama settings:", error);
  }
};

const BrowserVoiceCommandContext = createContext({
  settings: DEFAULT_SETTINGS,
  runtime: DEFAULT_RUNTIME,
  setEnabled: () => {},
  setRuntime: () => {},
  requestEnable: async () => false,
  requestDisable: () => {},
  registerController: () => () => {},
});

export function BrowserVoiceCommandProvider({ children }) {
  const [settings, setSettings] = useState(readStoredSettings);
  const [runtime, setRuntimeState] = useState(DEFAULT_RUNTIME);
  const controllerRef = useRef(null);

  const setEnabled = useCallback((enabled) => {
    const next = { enabled: Boolean(enabled) };
    setSettings(next);
    persistSettings(next);
  }, []);

  const setRuntime = useCallback((patch) => {
    setRuntimeState((prev) => ({
      ...prev,
      ...(typeof patch === "function" ? patch(prev) : patch),
    }));
  }, []);

  const registerController = useCallback((controller) => {
    controllerRef.current = controller || null;
    return () => {
      if (controllerRef.current === controller) controllerRef.current = null;
    };
  }, []);

  const requestEnable = useCallback(async () => {
    try {
      if (!controllerRef.current?.enable) {
        setRuntime({
          state: "error",
          live: false,
          error: "Hey Sama is still initializing. Please try again.",
        });
        return false;
      }
      const result = await controllerRef.current.enable();
      if (result === false) return false;
      setEnabled(true);
      return true;
    } catch (error) {
      setRuntime({
        state: "error",
        live: false,
        error: error?.message || "Unable to enable Hey Sama.",
      });
      return false;
    }
  }, [setEnabled, setRuntime]);

  const requestDisable = useCallback(() => {
    try {
      controllerRef.current?.disable?.();
    } finally {
      setEnabled(false);
      setRuntime({
        state: "off",
        live: false,
        level: 0,
        message: "",
        error: "",
      });
    }
  }, [setEnabled, setRuntime]);

  const value = useMemo(
    () => ({
      settings,
      runtime,
      setEnabled,
      setRuntime,
      requestEnable,
      requestDisable,
      registerController,
    }),
    [
      registerController,
      requestDisable,
      requestEnable,
      runtime,
      setEnabled,
      setRuntime,
      settings,
    ]
  );

  return (
    <BrowserVoiceCommandContext.Provider value={value}>
      {children}
    </BrowserVoiceCommandContext.Provider>
  );
}

export const useBrowserVoiceCommandSettings = () =>
  useContext(BrowserVoiceCommandContext);
